import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../reduxStore';
import { hideLoader, showLoader } from './loaderSlice';
import { showErrorSnackbar, showSuccessSnackbar } from './snackbarSlice';
import {
  generateInnerProductionPlanHistory,
  generateOuterProductionPlan,
  generateProductionPlanByStore
} from '../../ApiServices/ProductionPlanServices';
import { t } from 'i18next';

export const initialState = {
  value: [],
  historyValue: { stores: [], _id: null },
  storeValue: [],
  loading: false,
  error: null,
  selectedValue: null,
  rowsPerPage: 10,
  pageNo: 0,
  filter: {}
};

const productionPlanSlice = createSlice({
  name: 'productionPlan',
  initialState: initialState,
  reducers: {
    clearProductionPlanState(state: any) {
      state.rowsPerPage = initialState.rowsPerPage;
      state.pageNo = initialState.pageNo;
      state.value = initialState.value;
      state.loading = false;
      state.error = null;
      state.selectedValue = initialState.selectedValue;
      state.historyValue = initialState.historyValue;
      state.storeValue = initialState.storeValue;
      state.filter = initialState.filter;
    }
  },
  extraReducers: builder => {
    builder.addCase(createOuterProductionPlan.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.value = action?.payload?.data;
      state.pageNo = action?.meta?.arg?.page;
      state.rowsPerPage = action?.meta?.arg?.limit;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(createOuterProductionPlan.rejected, (state: any, action: any) => {
      state.error = action?.payload?.error?.message;
      state.loading = false;
      state.selectedValue = initialState?.selectedValue;
    });
    builder.addCase(createInnerProductionPlanHistory.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.historyValue = action?.payload?.data;
    });
    builder.addCase(createInnerProductionPlanHistory.rejected, (state: any, action: any) => {
      state.loading = initialState.loading;
      state.historyValue = initialState.historyValue;
      state.error = action?.payload;
    });
    builder.addCase(createByStoreProductionPlan.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.storeValue = action?.payload?.data;
    });
    builder.addCase(createByStoreProductionPlan.rejected, (state: any, action: any) => {
      state.loading = initialState.loading;
      state.storeValue = initialState.storeValue;
      state.error = action?.payload;
    });
  }
});

export const productionPlanState = (state: RootState) => state?.productionPlan;
export const { clearProductionPlanState } = productionPlanSlice.actions;
export default productionPlanSlice.reducer;

export const createOuterProductionPlan = createAsyncThunk(
  'productionPlan/generateOuterProductionPlan',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_ORDER_DETAILS') }));
      let response: any = await generateOuterProductionPlan(payload);
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const createInnerProductionPlanHistory = createAsyncThunk(
  'productionPlan/generateInnerProductionPlanHistory',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      let response: any = await generateInnerProductionPlanHistory(payload);
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const createByStoreProductionPlan = createAsyncThunk(
  'productionPlan/generateProductionPlanByStore',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      let response: any = await generateProductionPlanByStore(payload);
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const downloadOuterProduction = createAsyncThunk(
  'stores/downloadOuterProduction',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_PRODUCTION_PLAN') }));
      let response: any = await generateOuterProductionPlan(payload);
      if (response === 200) dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }));
      else dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILS') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);
